import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import Definition from './Definition';
import MarkdownRenderer from '../Common/MarkdownRenderer';
import Example from './Example';

const ConceptBlock = ({
  block,
  blockIdx,
  isTeacher = false,
  notebookId = null,
  sectionTitle = null,
  onUpdate = null,
  onEdit = null,
  onDelete = null,
  deleting = false,
}) => {
  const hasDefinition = block.definition && block.definition.trim();
  const hasNotes = block.notes && block.notes.length > 0;
  const hasTheorems = block.theorems && block.theorems.some(t => t.theorem || t.proof);
  const hasExamples = block.examples && block.examples.length > 0;
  const hasContent = hasDefinition || hasNotes || hasTheorems || hasExamples;

  if (!hasContent && !isTeacher) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3.5,
        mb: 4,
        border: '1px solid',
        borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'grey.200',
        borderRadius: 3,
        backgroundColor: 'background.paper',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'grey.300',
        }
      }}
    >
      {/* Concept Block 标题 */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              color: 'text.primary',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 0.5
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                fontSize: '0.875rem',
                fontWeight: 600
              }}
            >
              {blockIdx + 1}
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                概念 {blockIdx + 1}
              </Typography>
              {hasDefinition && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary',
                    fontWeight: 400,
                    lineHeight: 1.5
                  }}
                >
                  {block.definition.substring(0, 60).replace(/\n/g, ' ')}
                  {block.definition.length > 60 ? '...' : ''}
                </Typography>
              )}
            </Box>
          </Typography>
        </Box>
        {isTeacher && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<EditIcon fontSize="small" />}
              onClick={() => onEdit && onEdit(blockIdx, block)}
              sx={{ textTransform: 'none' }}
            >
              修改概念块
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon fontSize="small" />}
              onClick={() => onDelete && onDelete(blockIdx)}
              disabled={deleting}
              sx={{ textTransform: 'none' }}
            >
              {deleting ? '删除中...' : '删除概念块'}
            </Button>
          </Stack>
        )}
      </Box>

      {/* 定义 */}
      {hasDefinition && (
        <Box sx={{ mb: 3 }}>
          <Definition 
            definition={block.definition} 
            isTeacher={false}
            notebookId={notebookId}
            sectionTitle={sectionTitle}
            blockIndex={blockIdx}
            onUpdate={onUpdate}
          />
        </Box>
      )}

      {/* Notes */}
      {hasNotes && (
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 600, 
              mb: 1.5, 
              color: 'text.secondary',
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            Notes
          </Typography>
          <Box
            component="ul"
            sx={{
              listStyleType: 'circle',
              pl: 3,
              mb: 0,
              '& li': {
                mb: 1.5,
                color: 'text.primary',
                lineHeight: 1.75,
                '&::marker': {
                  color: 'text.secondary'
                },
                '& *': {
                  color: 'text.primary !important',
                }
              }
            }}
          >
            {block.notes.map((note, noteIdx) => (
              <Box
                key={noteIdx}
                component="li"
                sx={{
                  color: 'text.primary',
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.100',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  mb: 1,
                  display: 'block',
                  '& > *': {
                    display: 'inline',
                    color: 'text.primary',
                    backgroundColor: 'transparent',
                  },
                  '& p': {
                    color: 'text.primary',
                    margin: 0,
                    backgroundColor: 'transparent',
                  },
                  '& div': {
                    backgroundColor: 'transparent',
                    color: 'text.primary',
                  },
                  '& span': {
                    backgroundColor: 'transparent',
                    color: 'text.primary',
                  },
                  '& strong': {
                    color: 'text.primary',
                  },
                  '& em': {
                    color: 'text.primary',
                  }
                }}
              >
                <MarkdownRenderer content={note} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* 定理 */}
      {hasTheorems && block.theorems.map((theorem, theoremIdx) => {
        if (!theorem.theorem && !theorem.proof) {
          return null;
        }

        return (
          <Box key={theoremIdx} sx={{ mb: 3 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'grey.200',
                borderLeft: '4px solid',
                borderLeftColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'grey.600',
                backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50',
                borderRadius: 2,
              }}
            >
              {/* 定理内容 */}
              {theorem.theorem && (
                <Box>
                  <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, fontSize: '1rem', mb: 2 }}>
                    定理
                  </Typography>
                  <Box sx={{ color: 'text.primary', mb: 2 }}>
                    <MarkdownRenderer content={theorem.theorem} />
                  </Box>
                </Box>
              )}

              {/* 证明内容 */}
              {theorem.proof && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    证明：
                  </Typography>
                  <MarkdownRenderer content={theorem.proof} />
                </Box>
              )}
            </Paper>
          </Box>
        );
      })}

      {/* 例子 */}
      {hasExamples && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
            相关例子
          </Typography>
          {block.examples.map((example, exampleIdx) => (
            <Box key={exampleIdx} sx={{ mb: 2 }}>
              <Example example={example} index={exampleIdx + 1} hideTitle={false} />
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default ConceptBlock;
